import React, { useCallback, useEffect, useRef } from "react";
import * as echarts from "echarts";
import { Card, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { useDataProvider, Loading } from "react-admin";
import { useNavigate } from "react-router-dom";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

const TRACKED_CVE_RESOURCE = "cve/tracked-cves";

function buildListSearchParams(filter: Record<string, unknown>): string {
  const displayedFilters = Object.fromEntries(
    Object.keys(filter).map((k) => [k, true])
  );
  const params = new URLSearchParams();
  params.set("filter", JSON.stringify(filter));
  params.set("displayedFilters", JSON.stringify(displayedFilters));
  return params.toString();
}

/**
 * RelationChart renders a les-miserables style graph of CVE releations by image and dependency for a specified product release.
 */
interface RelationChartProps {
  releaseName: string;
  releaseVersion: string;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

const RelationChart: React.FC<RelationChartProps> = ({
  releaseName,
  releaseVersion,
  expanded = false,
  onToggleExpand,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const chartRef = useRef<HTMLDivElement>(null);
  const dataProvider = useDataProvider();
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartData, setChartData] = React.useState<any | null>(null);

  const navigateToFilteredList = useCallback(
    (filter: Record<string, unknown>) => {
      const search = buildListSearchParams({
        vulnerableReleaseName: releaseName,
        vulnerableReleaseVersion: releaseVersion,
        ...filter,
      });
      navigate(`/${TRACKED_CVE_RESOURCE}?${search}`);
    },
    [navigate, releaseName, releaseVersion]
  );

  useEffect(() => {
    let isMounted = true;
    setChartData(null); // Reset to loading state on dependency change

    const fetchData = async () => {
      try {
        const response = await dataProvider.getChart(
          "cve/analytics/relation-chart",
          {
            releaseName: releaseName,
            releaseVersion: releaseVersion,
          }
        );
        if (!isMounted) return;
        setChartData(response);
      } catch (error) {
        if (isMounted) {
          setChartData(null);
          console.error("Error fetching dashboard data:", error);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [dataProvider, releaseName, releaseVersion]);

  useEffect(() => {
    if (!chartData) return;
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }
    const option: echarts.EChartsOption = {
      title: {
        text: "Vulnerable Dependency Relations",
        left: "center",
        top: "15em",
        textStyle: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
          fontWeight: "bold",
          fontSize: "1.5em",
        },
      },
      tooltip: {
        trigger: "item",
        enterable: true,
        confine: true,
        formatter: (raw: unknown) => {
          const params = raw as {
            dataType?: string;
            data?: Record<string, unknown>;
            name?: string;
          };
          const { dataType, data } = params;
          if (dataType === "edge" && data) {
            const cve = String(data.value ?? "");
            const sev = data.severity != null ? String(data.severity) : "—";
            return `<div style="max-width:280px"><strong>${cve}</strong><br/>Severity: ${sev}<br/><span style="opacity:.85">Click edge → Tracked CVE detail</span></div>`;
          }
          if (dataType === "node" && data) {
            const cat = data.category;
            const label = String(data.name ?? params.name ?? "");
            if (cat === 0 || cat === "0") {
              const repo = String(data.imageRepository ?? "");
              const tag = String(data.imageTag ?? "");
              return `<div style="max-width:280px"><strong>Image</strong><br/>${label}<br/>${repo ? `${repo} ` : ""}${tag ? `:${tag}` : ""}<br/><span style="opacity:.85">Click → filter Tracked CVEs</span></div>`;
            }
            return `<div style="max-width:280px"><strong>Dependency</strong><br/>${label}<br/><span style="opacity:.85">Click → filter Tracked CVEs</span></div>`;
          }
          return "";
        },
      },
      toolbox: {
        show: true,
        orient: "vertical",
        left: "right",
        top: "bottom",
        feature: {
          dataView: { show: true, readOnly: false },
          restore: { show: true },
          saveAsImage: { show: true },
        },
      },
      legend: {
        data: chartData.categories,
        left: "left",
        top: "center",
        orient: "vertical",
        textStyle: {
          color: theme.palette.mode === "dark" ? "#fff" : "#222",
          fontWeight: "bold",
          fontSize: "0.8em",
        },
      },
      animationDuration: 1500,
      animationEasingUpdate: "quinticInOut",
      series: [
        {
          top: "65em",
          type: "graph",
          legendHoverLink: false,
          layout: "circular",
          data: chartData.nodes,
          links: chartData.links,
          categories: chartData.categories,
          roam: true,
          zoom: 0.9,
          label: {
            position: "right",
            formatter: "{b}",
            color: theme.palette.mode === "dark" ? "#fff" : "#222",
            fontWeight: "bold",
            fontSize: "12px",
            backgroundColor: theme.palette.mode === "dark" ? "#222" : "#fff",
            padding: 5,
            borderRadius: 10,
          },
          labelLayout: {
            hideOverlap: true,
          },
          lineStyle: {
            color: "source",
            curveness: 0.3,
          },
          emphasis: {
            focus: "adjacency",
            lineStyle: {
              width: 10,
            },
          },
        },
      ],
    };
    chartInstance.current.setOption(option);
  }, [chartData, theme.palette.mode]);

  useEffect(() => {
    const chart = chartInstance.current;
    if (!chart || !chartData) return;

    const handleClick = (raw: unknown) => {
      const params = raw as {
        dataType?: string;
        data?: Record<string, unknown>;
      };
      if (params.dataType === "edge" && params.data?.trackedCveId) {
        const id = String(params.data.trackedCveId);
        navigate(`/${TRACKED_CVE_RESOURCE}/${encodeURIComponent(id)}/show`);
        return;
      }
      if (params.dataType === "node" && params.data) {
        const node = params.data as {
          category?: number | string;
          name?: string;
          imageRepository?: string;
          imageTag?: string;
        };
        const cat = node.category;
        if (cat === 0 || cat === "0") {
          const imgFilter: Record<string, unknown> = {};
          if (node.imageRepository) imgFilter.imageRepository = node.imageRepository;
          if (node.imageTag) imgFilter.imageTag = node.imageTag;
          if (Object.keys(imgFilter).length === 0 && node.name) {
            imgFilter.ql_name = node.name;
          }
          navigateToFilteredList(imgFilter);
        } else if (node.name) {
          navigateToFilteredList({
            vulnerableDependencyName: node.name,
          });
        }
      }
    };

    chart.on("click", handleClick);
    return () => {
      chart.off("click", handleClick);
    };
  }, [chartData, navigate, navigateToFilteredList]);

  return (
    <Card
      sx={{
        height: expanded ? 1000 : 400,
        width: "100%",
        position: "relative",
      }}
    >
      {onToggleExpand && (
        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
          {expanded ? (
            <CloseFullscreenIcon onClick={onToggleExpand} />
          ) : (
            <OpenInFullIcon onClick={onToggleExpand} />
          )}
        </Box>
      )}
      {!chartData ? (
        <Loading />
      ) : !Array.isArray(chartData.nodes) || chartData.nodes.length === 0 ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            No Data Found
          </Typography>
        </Box>
      ) : (
        <Box
          ref={chartRef}
          sx={{ height: "100%", width: "100%", cursor: "pointer" }}
        />
      )}
    </Card>
  );
};

export default RelationChart;
